/*
 * USMapInteractive.tsx (Typed)
 * ---------------------------------------------------------------------------
 * A production-ready React + TypeScript demo component that renders a US map
 * with hover and click interactions. Uses react-simple-maps for SVG geographies
 * and React-Bootstrap for layout and UI. Designed as a drop-in component.
 *
 * Dependencies (install):
 *   npm i react-simple-maps d3-geo topojson-client react-bootstrap bootstrap
 *   npm i -D @types/d3-geo
 *
 * If your TS compiler still cannot resolve types for react-simple-maps,
 * add a file: src/types/react-simple-maps.d.ts with:
 *   declare module "react-simple-maps";
 *
 * Bootstrap CSS (in your app entry, e.g., src/main.tsx or src/index.tsx):
 *   import "bootstrap/dist/css/bootstrap.min.css";
 *
 * Example usage:
 *   <USMapInteractive onStateSelect={(code, name) => console.log(code, name)} />
 */

import React, { useMemo, useRef, useState } from "react";
import { Container, Row, Col, Card, Badge, Form, Spinner, OverlayTrigger, Tooltip, Button } from "react-bootstrap";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

// Public TopoJSON of US states (no counties). You may self-host.
const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// --- Types ------------------------------------------------------------------
export type StateCode =
  | "AL" | "AK" | "AZ" | "AR" | "CA" | "CO" | "CT" | "DE" | "FL" | "GA"
  | "HI" | "ID" | "IL" | "IN" | "IA" | "KS" | "KY" | "LA" | "ME" | "MD"
  | "MA" | "MI" | "MN" | "MS" | "MO" | "MT" | "NE" | "NV" | "NH" | "NJ"
  | "NM" | "NY" | "NC" | "ND" | "OH" | "OK" | "OR" | "PA" | "RI" | "SC"
  | "SD" | "TN" | "TX" | "UT" | "VT" | "VA" | "WA" | "WV" | "WI" | "WY" | "DC";

export interface StateInfo {
  code: StateCode;
  name: string;
  capital?: string;
  population?: string; // keep as string for easy formatting
  funFact?: string;
}

// react-simple-maps doesn't export a strict type for the feature used here, so
// we define a minimal shape that matches the fields we access.
export interface RSMGeography {
  id: string | number;
  rsmKey: string;
  properties: { name?: string; [k: string]: unknown };
}

interface USMapInteractiveProps {
  height?: number; // SVG height in px (width is responsive)
  highlightColor?: string; // CSS color for hover and selected
  onStateSelect?: (code: StateCode, name: string, info?: StateInfo) => void;
}

// --- Demo dataset (replace with your API) -----------------------------------
const DEMO_STATE_INFO: Partial<Record<StateCode, StateInfo>> = {
  CO: { code: "CO", name: "Colorado", capital: "Denver", population: "5.9M", funFact: "Over 300 days of sunshine per year." },
  CA: { code: "CA", name: "California", capital: "Sacramento", population: "39M", funFact: "If it were a country, it'd be a top 5 economy." },
  TX: { code: "TX", name: "Texas", capital: "Austin", population: "30M", funFact: "Bigger than many countries in land area." },
  NY: { code: "NY", name: "New York", capital: "Albany", population: "19.6M", funFact: "Home to Niagara Falls and NYC." },
  FL: { code: "FL", name: "Florida", capital: "Tallahassee", population: "22M", funFact: "Has the longest coastline in the contiguous US." },
};

function fetchStateInfo(code: StateCode): Promise<StateInfo | undefined> {
  // Simulate async lookup; replace with real fetch/axios call as needed
  return new Promise((resolve) => {
    setTimeout(() => resolve(DEMO_STATE_INFO[code]), 350);
  });
}

// Utility: map FIPS to USPS code; react-simple-maps exposes id as numeric FIPS
// Source: https://en.wikipedia.org/wiki/Federal_Information_Processing_Standards_state_code
const FIPS_TO_USPS: Record<string, StateCode> = {
  "01": "AL", "02": "AK", "04": "AZ", "05": "AR", "06": "CA", "08": "CO",
  "09": "CT", "10": "DE", "11": "DC", "12": "FL", "13": "GA", "15": "HI",
  "16": "ID", "17": "IL", "18": "IN", "19": "IA", "20": "KS", "21": "KY",
  "22": "LA", "23": "ME", "24": "MD", "25": "MA", "26": "MI", "27": "MN",
  "28": "MS", "29": "MO", "30": "MT", "31": "NE", "32": "NV", "33": "NH",
  "34": "NJ", "35": "NM", "36": "NY", "37": "NC", "38": "ND", "39": "OH",
  "40": "OK", "41": "OR", "42": "PA", "44": "RI", "45": "SC", "46": "SD",
  "47": "TN", "48": "TX", "49": "UT", "50": "VT", "51": "VA", "53": "WA",
  "54": "WV", "55": "WI", "56": "WY",
};

const DEFAULT_HEIGHT = 520;

export default function USMapInteractive({
  height = DEFAULT_HEIGHT,
  highlightColor = "#0d6efd",
  onStateSelect,
}: USMapInteractiveProps) {
  const [hoverCode, setHoverCode] = useState<StateCode | null>(null);
  const [selectedCode, setSelectedCode] = useState<StateCode | null>(null);
  const [selectedInfo, setSelectedInfo] = useState<StateInfo | undefined>();
  const [loading, setLoading] = useState(false);
  const [showLabels, setShowLabels] = useState(true);

  const tooltipTarget = useRef<HTMLDivElement | null>(null);

  const mapStyle = useMemo<React.CSSProperties>(() => ({
    width: "100%",
    height,
  }), [height]);

  const regionFill = (code: StateCode, isSelected: boolean, isHover: boolean) => {
    if (isSelected) return highlightColor;
    if (isHover) return "#63a3ff"; // lighter hover
    return "#f2f4f7"; // base
  };

  const regionStroke = (code: StateCode, isSelected: boolean, isHover: boolean) => {
    if (isSelected) return "#063a88";
    if (isHover) return "#2f6feb";
    return "#cbd5e1";
  };

  const handleClick = async (code: StateCode, name: string) => {
    setSelectedCode(code);
    setSelectedInfo(undefined);
    setLoading(true);
    try {
      const info = await fetchStateInfo(code);
      setSelectedInfo(info ?? { code, name });
      onStateSelect?.(code, name, info);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="py-3">
      <Row className="g-3">
        <Col lg={8}>
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <strong>US Map</strong>
                {hoverCode && (
                  <Badge bg="secondary" className="ms-2">Hover: {hoverCode}</Badge>
                )}
                {selectedCode && (
                  <Badge bg="primary" className="ms-2">Selected: {selectedCode}</Badge>
                )}
              </div>
              <Form.Check
                type="switch"
                id="label-switch"
                label="Show state codes on hover"
                checked={showLabels}
                onChange={(e) => setShowLabels(e.currentTarget.checked)}
              />
            </Card.Header>
            <Card.Body>
              <div ref={tooltipTarget} />
              <div style={mapStyle}>
                <ComposableMap projection="geoAlbersUsa" style={{ width: "100%", height: "100%" }}>
                  <Geographies geography={geoUrl}>
                    {({ geographies }: { geographies: RSMGeography[] }) => (
                      <>
                        {geographies.map((geo: RSMGeography) => {
                          // Each geo has an id (FIPS) and properties.name
                          const fips = String(geo.id).padStart(2, "0");
                          const code = FIPS_TO_USPS[fips];
                          const name = (geo.properties?.name as string) ?? "";
                          // Some features include non-state areas; filter to mapped states
                          if (!code) return null;

                          const isHover = hoverCode === code;
                          const isSelected = selectedCode === code;

                          const geography = (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo as unknown as any}
                              onMouseEnter={() => setHoverCode(code)}
                              onMouseLeave={() => setHoverCode((prev) => (prev === code ? null : prev))}
                              onClick={() => handleClick(code, name)}
                              style={{
                                default: {
                                  fill: regionFill(code, isSelected, isHover),
                                  stroke: regionStroke(code, isSelected, isHover),
                                  strokeWidth: 0.8,
                                  outline: "none",
                                  transition: "fill 120ms ease, stroke 120ms ease",
                                  cursor: "pointer",
                                },
                                hover: {
                                  fill: regionFill(code, isSelected, true),
                                  stroke: regionStroke(code, isSelected, true),
                                  outline: "none",
                                  cursor: "pointer",
                                },
                                pressed: {
                                  fill: regionFill(code, true, false),
                                  stroke: regionStroke(code, true, false),
                                },
                              }}
                            />
                          );

                          return showLabels ? (
                            <OverlayTrigger
                              key={geo.rsmKey}
                              placement="top"
                              overlay={<Tooltip id={`tt-${code}`}>{code} — {name}</Tooltip>}
                            >
                              {geography as unknown as React.ReactElement}
                            </OverlayTrigger>
                          ) : (
                            geography
                          );
                        })}
                      </>
                    )}
                  </Geographies>
                </ComposableMap>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="shadow-sm h-100">
            <Card.Header>
              <strong>State Details</strong>
            </Card.Header>
            <Card.Body>
              {!selectedCode && (
                <div className="text-muted">Click a state to see details.</div>
              )}

              {selectedCode && (
                <div>
                  {loading ? (
                    <div className="d-flex align-items-center gap-2">
                      <Spinner animation="border" size="sm" />
                      <span>Loading info…</span>
                    </div>
                  ) : (
                    <>
                      <h5 className="mb-2">
                        {selectedInfo?.name ?? selectedCode} {selectedInfo?.capital && (
                          <Badge bg="info" className="ms-2">Capital: {selectedInfo?.capital}</Badge>
                        )}
                      </h5>
                      <ul className="mb-3">
                        {selectedInfo?.population && <li>Population: {selectedInfo.population}</li>}
                        {selectedInfo?.funFact && <li>Fun fact: {selectedInfo.funFact}</li>}
                        {!selectedInfo?.population && !selectedInfo?.funFact && (
                          <li className="text-muted">No demo data. Replace with your API results.</li>
                        )}
                      </ul>
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => window.open(`https://en.wikipedia.org/wiki/${encodeURIComponent(selectedInfo?.name ?? selectedCode)}`, "_blank")}
                        >
                          Open Wiki
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setSelectedCode(null)}>Clear</Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </Card.Body>
            <Card.Footer className="text-muted small">
              Tip: Replace the demo dataset with a real fetch based on the 2-letter
              state code. The component exposes `onStateSelect` for integration.
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
